import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArticleIcon from '@mui/icons-material/Article';
import BoltIcon from '@mui/icons-material/Bolt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getFundRules, updateFundRules } from '../../../Utils/fundRules';
import Documentation from './Documentation';

/** Format minutes as "240 min (~4h)". */
const fmtMinutesAsHours = (min) => {
  const m = Number(min);
  if (!Number.isFinite(m)) return '—';
  return m >= 60 ? `${m} min (~${Math.round(m / 60)}h)` : `${m} min`;
};

/** Operating-window scenario helpers — illustrative, whole-hour granularity. */
const isHourWithinWindow = (hourOfDay, startHour, endHour) => {
  const h = ((hourOfDay % 24) + 24) % 24;
  if (startHour === endHour) return true;
  if (startHour < endHour) return h >= startHour && h < endHour;
  return h >= startHour || h < endHour;
};

const computeWindowScenario = ({ requestHour, startHour, endHour, acceptDelayH, payDelayH }) => {
  const nextOpen = (absHour) => {
    let cur = absHour;
    let guard = 0;
    while (!isHourWithinWindow(cur, startHour, endHour) && guard < 72) {
      cur += 1;
      guard += 1;
    }
    return cur;
  };
  const acceptEligible = requestHour + acceptDelayH;
  const acceptDeferred = !isHourWithinWindow(acceptEligible, startHour, endHour);
  const acceptActual = nextOpen(acceptEligible);
  const payEligible = acceptActual + payDelayH;
  const payDeferred = !isHourWithinWindow(payEligible, startHour, endHour);
  const payActual = nextOpen(payEligible);
  return { acceptEligible, acceptActual, acceptDeferred, payEligible, payActual, payDeferred };
};

const fmtAbsHour = (absHour) => {
  const dayOffset = Math.floor(absHour / 24);
  const hourOfDay = ((absHour % 24) + 24) % 24;
  const hh = String(hourOfDay).padStart(2, '0');
  return `${hh}:00${dayOffset > 0 ? ` (+${dayOffset}d)` : ''}`;
};

// Shared field styles for dark theme
const FIELD_LABEL = { sx: { color: "rgba(255,255,255,0.6)" } };
const FIELD_INPUT = { sx: { color: "#fff" } };
const FIELD_HELPER = { sx: { color: "rgba(255,255,255,0.45)" } };

const SectionCard = ({ title, description, children, editSection, editMode, onEdit, onSave, onCancel, saving }) => (
  <Card sx={{
    mb: 3,
    background: "linear-gradient(135deg, #252535 0%, #1a1a28 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 3,
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>{title}</Typography>
          {description && (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 0.25 }}>{description}</Typography>
          )}
        </Box>
        {editSection && (
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0, ml: 2 }}>
            {!editMode ? (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={onEdit}
                variant="outlined"
                sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)", "&:hover": { borderColor: "#2af598", color: "#2af598" } }}
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                  onClick={onSave}
                  variant="contained"
                  disabled={saving}
                  sx={{ bgcolor: "#2af598", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#1de585" } }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                  variant="outlined"
                  sx={{ color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.2)" }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 2.5, borderColor: "rgba(255,255,255,0.07)" }} />
      {children}
    </CardContent>
  </Card>
);

const FundRules = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [rules, setRules] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const fundRules = await getFundRules();
      setRules(fundRules);
      setEditedValues(fundRules);
    } catch (error) {
      console.error('Error loading fund rules:', error);
      toast.error('Failed to load fund rules');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section) => setEditMode({ ...editMode, [section]: true });
  const handleCancel = (section) => {
    setEditMode({ ...editMode, [section]: false });
    setEditedValues({ ...editedValues, [section]: rules[section] });
  };
  const handleSave = (section) => {
    setPendingChanges({ section, values: editedValues[section] });
    setConfirmDialog(true);
  };

  const confirmSave = async () => {
    const { section } = pendingChanges;
    setConfirmDialog(false);
    setSavingSection(section);
    try {
      const newRules = await updateFundRules({ [section]: editedValues[section] }, 'admin@fiddux.com');
      setRules(newRules);
      setEditedValues(newRules);
      setEditMode({ ...editMode, [section]: false });
      toast.success('Fund rules updated successfully!');
    } catch (error) {
      console.error('Error updating fund rules:', error);
      toast.error('Failed to update fund rules');
    } finally {
      setSavingSection(null);
    }
  };

  const handleFieldChange = (section, field, value) => {
    setEditedValues({ ...editedValues, [section]: { ...editedValues[section], [field]: value } });
  };

  const handleNestedFieldChange = (section, parent, field, value) => {
    setEditedValues({
      ...editedValues,
      [section]: { ...editedValues[section], [parent]: { ...editedValues[section][parent], [field]: value } }
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <CircularProgress sx={{ color: "#2af598" }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: "100%",
      px: { xs: 2, md: 3 },
      pt: { xs: "70px", md: 3 },
      pb: 4,
      "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.45)" },
      "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
      "& .MuiInputLabel-root.Mui-focused": { color: "#2af598" },
      "& .MuiInputBase-input": { color: "#fff" },
      "& .MuiInputBase-input.Mui-disabled": { color: "rgba(255,255,255,0.4)", WebkitTextFillColor: "rgba(255,255,255,0.4)" },
      "& .MuiFormControlLabel-label": { color: "rgba(255,255,255,0.85)" },
      "& .MuiSwitch-track": { bgcolor: "rgba(255,255,255,0.2)" },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.15)" },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.35)" },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2af598" },
      "& .MuiAlert-message": { color: "rgba(255,255,255,0.9)" },
      "& .MuiAlert-message *": { color: "rgba(255,255,255,0.9)" },
    }}>
      {/* Header */}
      <Paper elevation={0} sx={{ background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)", borderRadius: 3, p: 3, mb: 3, border: "1px solid rgba(255,255,255,0.1)" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(42,245,152,0.15)", borderRadius: 2, border: "1px solid rgba(42,245,152,0.3)" }}>
              <GavelIcon sx={{ color: "#2af598", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800, lineHeight: 1.2 }}>Fund Rules</Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25 }}>
                Commission · Deposits · Withdrawals · Auto-Pay · Monthly Closing
              </Typography>
            </Box>
          </Box>
          {rules && (
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block" }}>
                Version <strong style={{ color: "#2af598" }}>{rules.version}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block" }}>
                Updated by {rules.updated_by || "—"} · {rules.updated_at?.toDate?.().toLocaleDateString() || "N/A"}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3, background: "#1e1e2e", border: "1px solid rgba(255,255,255,0.08)" }}>
        <Tabs
          value={currentTab}
          onChange={(_, v) => setCurrentTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": { color: "rgba(255,255,255,0.5)", minHeight: 52 },
            "& .MuiTab-root.Mui-selected": { color: "#2af598" },
            "& .MuiTabs-indicator": { bgcolor: "#2af598" },
          }}
        >
          <Tab icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} label="General" iconPosition="start" />
          <Tab icon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />} label="Withdrawals" iconPosition="start" />
          <Tab icon={<BoltIcon sx={{ fontSize: 18 }} />} label="Auto-Pay" iconPosition="start" />
          <Tab icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />} label="Monthly Closing" iconPosition="start" />
          <Tab icon={<ArticleIcon sx={{ fontSize: 18 }} />} label="Documentation" iconPosition="start" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} label="History" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: General Settings */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SectionCard
              title="Commission Rates"
              description="Applied at monthly closing based on performance"
              editSection="commission"
              editMode={editMode.commission}
              onEdit={() => handleEdit('commission')}
              onSave={() => handleSave('commission')}
              onCancel={() => handleCancel('commission')}
              saving={savingSection === 'commission'}
            >
              <TextField
                fullWidth
                label="Positive Performance Commission (%)"
                type="number"
                value={editedValues?.commission?.positive_performance ?? 0}
                onChange={(e) => handleFieldChange('commission', 'positive_performance', parseFloat(e.target.value))}
                disabled={!editMode.commission}
                sx={{ mb: 2 }}
                helperText="Applied when monthly performance is positive"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
              <TextField
                fullWidth
                label="Negative Performance Commission (%)"
                type="number"
                value={editedValues?.commission?.negative_performance ?? 0}
                onChange={(e) => handleFieldChange('commission', 'negative_performance', parseFloat(e.target.value))}
                disabled={!editMode.commission}
                helperText="Applied when monthly performance is negative"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SectionCard
              title="Deposit Rules"
              description="Timing and approval settings for client deposits"
              editSection="deposit_rules"
              editMode={editMode.deposit_rules}
              onEdit={() => handleEdit('deposit_rules')}
              onSave={() => handleSave('deposit_rules')}
              onCancel={() => handleCancel('deposit_rules')}
              saving={savingSection === 'deposit_rules'}
            >
              <TextField
                fullWidth
                label="Cutoff Hour (UTC)"
                type="number"
                value={editedValues?.deposit_rules?.cutoff_hour_utc ?? 21}
                onChange={(e) => handleFieldChange('deposit_rules', 'cutoff_hour_utc', parseInt(e.target.value))}
                disabled={!editMode.deposit_rules}
                sx={{ mb: 2 }}
                helperText="Deposits before this hour are approved same day"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
              <TextField
                fullWidth
                label="Approval Window (hours)"
                type="number"
                value={editedValues?.deposit_rules?.approval_window_hours ?? 24}
                onChange={(e) => handleFieldChange('deposit_rules', 'approval_window_hours', parseInt(e.target.value))}
                disabled={!editMode.deposit_rules}
                helperText="Time to move funds into trading strategy"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
              <Alert severity="info" sx={{ mt: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.8)" } }}>
                <Typography variant="caption">Deposits start trading on D+1 (day after approval)</Typography>
              </Alert>
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Withdrawal Rules */}
      {currentTab === 1 && (
        <SectionCard
          title="Withdrawal & Conditional Offer Settings"
          description="Controls withdrawal processing thresholds and conditional offer flow"
          editSection="withdrawal_rules"
          editMode={editMode.withdrawal_rules}
          onEdit={() => handleEdit('withdrawal_rules')}
          onSave={() => handleSave('withdrawal_rules')}
          onCancel={() => handleCancel('withdrawal_rules')}
          saving={savingSection === 'withdrawal_rules'}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Large Withdrawal Threshold (%)"
                type="number"
                value={editedValues?.withdrawal_rules?.large_withdrawal_threshold ?? 80}
                onChange={(e) => handleFieldChange('withdrawal_rules', 'large_withdrawal_threshold', parseFloat(e.target.value))}
                disabled={!editMode.withdrawal_rules}
                helperText="Withdrawals above this % of balance trigger conditional offer"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", mt: 2, mb: 2, fontWeight: 600 }}>
                Conditional Offer Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.withdrawal_rules?.conditional_offer?.enabled ?? false}
                    onChange={(e) => handleNestedFieldChange('withdrawal_rules', 'conditional_offer', 'enabled', e.target.checked)}
                    disabled={!editMode.withdrawal_rules}
                    sx={{ "& .MuiSwitch-thumb": { bgcolor: editedValues?.withdrawal_rules?.conditional_offer?.enabled ? "#2af598" : undefined } }}
                  />
                }
                label="Enable Conditional Offers"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.withdrawal_rules?.conditional_offer?.allow_custom_time ?? false}
                    onChange={(e) => handleNestedFieldChange('withdrawal_rules', 'conditional_offer', 'allow_custom_time', e.target.checked)}
                    disabled={!editMode.withdrawal_rules}
                  />
                }
                label="Allow Custom Response Time"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Default Response Time (min)" type="number"
                value={editedValues?.withdrawal_rules?.conditional_offer?.default_time_minutes ?? 60}
                onChange={(e) => handleNestedFieldChange('withdrawal_rules', 'conditional_offer', 'default_time_minutes', parseInt(e.target.value))}
                disabled={!editMode.withdrawal_rules}
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Min Response Time (min)" type="number"
                value={editedValues?.withdrawal_rules?.conditional_offer?.min_time_minutes ?? 15}
                onChange={(e) => handleNestedFieldChange('withdrawal_rules', 'conditional_offer', 'min_time_minutes', parseInt(e.target.value))}
                disabled={!editMode.withdrawal_rules}
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Max Response Time (min)" type="number"
                value={editedValues?.withdrawal_rules?.conditional_offer?.max_time_minutes ?? 240}
                onChange={(e) => handleNestedFieldChange('withdrawal_rules', 'conditional_offer', 'max_time_minutes', parseInt(e.target.value))}
                disabled={!editMode.withdrawal_rules}
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }} />
          <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", mb: 1.5, fontWeight: 600 }}>
            Full Withdrawal Timer
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>Auto-cancel after</Typography>
            <Chip
              label={`${editedValues?.full_withdraw_timer_hours ?? "—"} h`}
              size="small"
              sx={{ bgcolor: "rgba(255,213,79,0.15)", color: "#ffd54f", border: "1px solid rgba(255,213,79,0.3)", fontWeight: 700 }}
            />
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
              (top-level <code>full_withdraw_timer_hours</code> — only full withdrawals auto-cancel; normal withdrawals never expire)
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 3, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
            <Typography variant="body2">
              Withdrawals stop trading immediately (D0) when approved. Negative performance applies to the entire account.
            </Typography>
          </Alert>
        </SectionCard>
      )}

      {/* Tab 2: Auto-Pay */}
      {currentTab === 2 && (
        <SectionCard
          title="Auto-Pay — Bybit on-chain payout"
          description="Read by the Cloud Function processAutoPayWithdrawal every minute (fund_rules/active.auto_pay)"
          editSection="auto_pay"
          editMode={editMode.auto_pay}
          onEdit={() => handleEdit('auto_pay')}
          onSave={() => handleSave('auto_pay')}
          onCancel={() => handleCancel('auto_pay')}
          saving={savingSection === 'auto_pay'}
        >
          <Grid container spacing={3}>
            {/* Master switches */}
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.auto_pay?.enabled ?? false}
                    onChange={(e) => handleFieldChange('auto_pay', 'enabled', e.target.checked)}
                    disabled={!editMode.auto_pay}
                    sx={{ "& .MuiSwitch-thumb": { bgcolor: editedValues?.auto_pay?.enabled ? "#2af598" : undefined } }}
                  />
                }
                label="Enable Auto-Pay"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.auto_pay?.exchange_enabled ?? false}
                    onChange={(e) => handleFieldChange('auto_pay', 'exchange_enabled', e.target.checked)}
                    disabled={!editMode.auto_pay}
                    sx={{ "& .MuiSwitch-thumb": { bgcolor: editedValues?.auto_pay?.exchange_enabled ? "#2af598" : undefined } }}
                  />
                }
                label="Bybit on-chain payout"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.auto_pay?.enforce_kyc ?? false}
                    onChange={(e) => handleFieldChange('auto_pay', 'enforce_kyc', e.target.checked)}
                    disabled={!editMode.auto_pay}
                    sx={{ "& .MuiSwitch-thumb": { bgcolor: editedValues?.auto_pay?.enforce_kyc ? "#2af598" : undefined } }}
                  />
                }
                label="Enforce KYC (require approved)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editedValues?.auto_pay?.operating_window_enabled ?? false}
                    onChange={(e) => handleFieldChange('auto_pay', 'operating_window_enabled', e.target.checked)}
                    disabled={!editMode.auto_pay}
                    sx={{ "& .MuiSwitch-thumb": { bgcolor: editedValues?.auto_pay?.operating_window_enabled ? "#2af598" : undefined } }}
                  />
                }
                label="Operating window (business hours)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", mt: 2, fontWeight: 600 }}>
                Amount limits (USD)
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Min amount" type="number"
                value={editedValues?.auto_pay?.min_amount ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'min_amount', parseFloat(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText="Below this → manual queue"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Max amount" type="number"
                value={editedValues?.auto_pay?.max_amount ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'max_amount', parseFloat(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText="Above this → manual review"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Daily cap" type="number"
                value={editedValues?.auto_pay?.daily_cap ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'daily_cap', parseFloat(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText="Total auto-paid in 24h"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
              <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", mt: 2, fontWeight: 600 }}>
                Timing windows
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Accept delay (minutes)" type="number"
                value={editedValues?.auto_pay?.accept_delay_minutes ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'accept_delay_minutes', parseInt(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText={`Request → auto-accept · ${fmtMinutesAsHours(editedValues?.auto_pay?.accept_delay_minutes)}`}
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Pay delay (minutes)" type="number"
                value={editedValues?.auto_pay?.pay_delay_minutes ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'pay_delay_minutes', parseInt(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText={`Accept → ready-to-pay · ${fmtMinutesAsHours(editedValues?.auto_pay?.pay_delay_minutes)}`}
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Max window (hours)" type="number"
                value={editedValues?.auto_pay?.max_window_hours ?? 0}
                onChange={(e) => handleFieldChange('auto_pay', 'max_window_hours', parseInt(e.target.value))}
                disabled={!editMode.auto_pay}
                helperText="Older requests are ignored"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
            <Typography variant="body2">
              Flow: client request → after <strong>Accept delay</strong> the withdrawal moves to <strong>Accepted</strong> (status 2) →
              after <strong>Pay delay</strong> it is sent on-chain via Bybit (status 5). Both timers are checked by the cron every minute.
            </Typography>
          </Alert>

          {/* Operating window (business hours) — master toggle is in the switches group above */}
          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>
              Operating window
            </Typography>
            <Chip
              size="small"
              label={editedValues?.auto_pay?.operating_window_enabled ? "ON — restricts hours" : "OFF — 24/7 (current behaviour)"}
              sx={{
                fontWeight: 700,
                bgcolor: editedValues?.auto_pay?.operating_window_enabled ? "rgba(42,245,152,0.15)" : "rgba(255,255,255,0.08)",
                color: editedValues?.auto_pay?.operating_window_enabled ? "#2af598" : "rgba(255,255,255,0.7)",
                border: `1px solid ${editedValues?.auto_pay?.operating_window_enabled ? "rgba(42,245,152,0.4)" : "rgba(255,255,255,0.2)"}`,
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 1.5 }}>
            Toggle it in the switches above. When ON, auto-pay only accepts and pays within business hours; outside the window requests are deferred to the next opening (never cancelled). Off by default — no effect on the current flow.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Open hour (0–23)" type="number"
                value={editedValues?.auto_pay?.operating_start_hour ?? 9}
                onChange={(e) => handleFieldChange('auto_pay', 'operating_start_hour', parseInt(e.target.value))}
                disabled={!editMode.auto_pay || !(editedValues?.auto_pay?.operating_window_enabled ?? false)}
                helperText="Start accepting/paying"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Close hour (0–23)" type="number"
                value={editedValues?.auto_pay?.operating_end_hour ?? 21}
                onChange={(e) => handleFieldChange('auto_pay', 'operating_end_hour', parseInt(e.target.value))}
                disabled={!editMode.auto_pay || !(editedValues?.auto_pay?.operating_window_enabled ?? false)}
                helperText="Stop (exclusive)"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Timezone" type="text"
                value={editedValues?.auto_pay?.operating_timezone ?? 'Europe/London'}
                disabled
                helperText="DST-aware (BST/GMT)"
                InputLabelProps={FIELD_LABEL} InputProps={FIELD_INPUT} FormHelperTextProps={FIELD_HELPER}
              />
            </Grid>
          </Grid>

          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mt: 2, mb: 1 }}>
            Example scenarios — window {String(Number(editedValues?.auto_pay?.operating_start_hour ?? 9)).padStart(2, '0')}:00–
            {String(Number(editedValues?.auto_pay?.operating_end_hour ?? 21)).padStart(2, '0')}:00 ·
            accept +{Math.round((editedValues?.auto_pay?.accept_delay_minutes || 0) / 60)}h ·
            pay +{Math.round((editedValues?.auto_pay?.pay_delay_minutes || 0) / 60)}h.
            Amber = deferred to the next opening (whole-hour illustration).
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Request", "Accept eligible", "Accepted", "Pay eligible", "Paid"].map((h) => (
                    <TableCell key={h} sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 700, borderColor: "rgba(255,255,255,0.08)" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const startHour = Number(editedValues?.auto_pay?.operating_start_hour ?? 9);
                  const endHour = Number(editedValues?.auto_pay?.operating_end_hour ?? 21);
                  const acceptDelayH = Math.max(0, Math.round((editedValues?.auto_pay?.accept_delay_minutes || 0) / 60));
                  const payDelayH = Math.max(0, Math.round((editedValues?.auto_pay?.pay_delay_minutes || 0) / 60));
                  const anchors = [8, 10, 16, 19, 22];
                  const cellSx = { color: "#fff", borderColor: "rgba(255,255,255,0.08)" };
                  const deferSx = { color: "#ffd54f", fontWeight: 700, borderColor: "rgba(255,255,255,0.08)" };
                  return anchors.map((requestHour) => {
                    const s = computeWindowScenario({ requestHour, startHour, endHour, acceptDelayH, payDelayH });
                    return (
                      <TableRow key={requestHour}>
                        <TableCell sx={cellSx}>{fmtAbsHour(requestHour)}</TableCell>
                        <TableCell sx={cellSx}>{fmtAbsHour(s.acceptEligible)}</TableCell>
                        <TableCell sx={s.acceptDeferred ? deferSx : cellSx}>{fmtAbsHour(s.acceptActual)}</TableCell>
                        <TableCell sx={cellSx}>{fmtAbsHour(s.payEligible)}</TableCell>
                        <TableCell sx={s.payDeferred ? deferSx : cellSx}>{fmtAbsHour(s.payActual)}</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Read-only: managed in Admin Settings */}
          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.08)" }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>
              Assets, networks, liquidity & KYC whitelist
            </Typography>
            <Button
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={() => navigate('/admin/admin-settings')}
              variant="outlined"
              sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)", "&:hover": { borderColor: "#2af598", color: "#2af598" } }}
            >
              Edit in Admin Settings
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>Supported assets</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {(editedValues?.auto_pay?.supported_assets || []).map((a) => (
                  <Chip key={a} label={a} size="small" sx={{ bgcolor: "rgba(42,245,152,0.15)", color: "#2af598", border: "1px solid rgba(42,245,152,0.3)", fontWeight: 700 }} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>Supported networks</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {(editedValues?.auto_pay?.supported_networks || []).map((n) => (
                  <Chip key={n} label={n} size="small" sx={{ bgcolor: "rgba(126,184,255,0.15)", color: "#7eb8ff", border: "1px solid rgba(126,184,255,0.3)", fontWeight: 700 }} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>KYC whitelist</Typography>
              <Chip
                label={(editedValues?.auto_pay?.kyc_whitelist_uids || []).length > 0
                  ? `${editedValues.auto_pay.kyc_whitelist_uids.length} UID(s) — restricted`
                  : "Empty — all users"}
                size="small"
                sx={{ bgcolor: "rgba(255,213,79,0.15)", color: "#ffd54f", border: "1px solid rgba(255,213,79,0.3)", fontWeight: 700 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mb: 0.5 }}>Liquidity wallets (read-only)</Typography>
              {(() => {
                const assets = (editedValues?.auto_pay?.supported_assets || []).filter((a) => ["USDT", "USDC"].includes(a));
                const networks = editedValues?.auto_pay?.supported_networks || [];
                const keys = assets.flatMap((a) => networks.map((n) => `${a}_${n}`));
                if (keys.length === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>—</Typography>
                  );
                }
                return keys.map((key) => {
                  const sep = key.indexOf("_");
                  const label = sep > 0 ? `${key.slice(0, sep)} / ${key.slice(sep + 1)}` : key;
                  return (
                  <Typography key={key} variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
                    {label}: {editedValues?.auto_pay?.liquidity_wallets?.[key] || "—"}
                  </Typography>
                  );
                });
              })()}
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* Tab 3: Monthly Closing */}
      {currentTab === 3 && (
        <SectionCard
          title="Monthly Closing Configuration"
          description="How monthly performance and commissions are calculated"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 0.75 }}>Day calculation</Typography>
              <Chip label={editedValues?.monthly_closing?.day_calculation_method || 'last_day_of_month'} size="small" sx={{ bgcolor: "rgba(42,245,152,0.15)", color: "#2af598", border: "1px solid rgba(42,245,152,0.3)", fontWeight: 700 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 0.75 }}>Performance calculation</Typography>
              <Chip label={editedValues?.monthly_closing?.performance_calculation || 'daily_weighted'} size="small" sx={{ bgcolor: "rgba(126,184,255,0.15)", color: "#7eb8ff", border: "1px solid rgba(126,184,255,0.3)", fontWeight: 700 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 0.75 }}>Commission application</Typography>
              <Chip label={editedValues?.monthly_closing?.commission_application || 'on_positive_only'} size="small" sx={{ bgcolor: "rgba(255,213,79,0.15)", color: "#ffd54f", border: "1px solid rgba(255,213,79,0.3)", fontWeight: 700 }} />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 3, "& .MuiAlert-message": { color: "rgba(255,255,255,0.8)" } }}>
            <Typography variant="body2">
              Monthly closing uses <strong>daily-weighted performance</strong>. Each client's balance is tracked daily with deposits (D+1) and withdrawals (D0) to calculate accurate monthly returns.
            </Typography>
          </Alert>
        </SectionCard>
      )}

      {/* Tab 4: Documentation */}
      {currentTab === 4 && <Documentation />}

      {/* Tab 5: History */}
      {currentTab === 5 && (
        <SectionCard title="Change History" description="All fund rule changes are logged in fund_rules_history">
          <Alert severity="info" sx={{ "& .MuiAlert-message": { color: "rgba(255,255,255,0.8)" } }}>
            <Typography variant="body2">
              History feature coming soon. All changes are logged in the <code>fund_rules_history</code> collection in Firestore.
            </Typography>
          </Alert>
        </SectionCard>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        PaperProps={{ sx: { bgcolor: "#1e1e2e", color: "#fff", borderRadius: 3, border: "1px solid rgba(255,255,255,0.1)" } }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: 700 }}>Confirm Fund Rule Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
            Are you sure you want to update <strong>{pendingChanges?.section}</strong>?
            This will affect all future monthly closings and withdrawals immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>Cancel</Button>
          <Button onClick={confirmSave} variant="contained" sx={{ bgcolor: "#2af598", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#1de585" } }}>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundRules;
